import { createContext, useContext, useEffect, useState } from 'react';
import { setPlanGate, resetPlanGate } from '../lib/planGate';
import api from '../services/api';
import { useAuth } from './AuthContext';

const FeatureContext = createContext({
  features: {},
  loading: true,
  isFeatureEnabled: () => true,
  // iter308y — Phase 7B: plan-level (tenant subscription) feature gate.
  // Separate from `features` (per-role toggle) because the two operate
  // on orthogonal axes: a plan can sell or hide a category entirely,
  // while role features restrict who within an admin's tenant uses it.
  planFeatures: [],
  isPlanFeatureEnabled: () => true,
  refreshFeatures: () => {}
});

export const useFeatures = () => useContext(FeatureContext);

export const FeatureProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [features, setFeatures] = useState({});
  const [planFeatures, setPlanFeatures] = useState([]);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [planError, setPlanError] = useState(false); // iter244 SEC F4 — fail-closed flag
  const [loading, setLoading] = useState(true);

  const fetchPlanFeatures = async () => {
    if (!token || !user) {
      resetPlanGate();
      setPlanFeatures([]);
      setPlanLoaded(true);
      setPlanError(false);
      return;
    }
    // Super admins manage all tenants — their UI must surface everything,
    // even features the SA's own tenant_registry row isn't on.
    if (user.role === 'super_admin') {
      setPlanGate(null);
      setPlanFeatures([]);
      setPlanLoaded(true);
      setPlanError(false);
      return;
    }
    try {
      const response = await api.get('/tenant/me');
      const directFeatures = Array.isArray(response.data?.features) ? response.data.features : [];
      // Navigation must reflect the same dependency-expanded entitlement set
      // used by the server. Example: Email Automation requires (and therefore
      // must reveal) Transactional & Marketing Email. Direct grants remain in
      // the API for billing display; runtime features drive usable surfaces.
      const runtimeFeatures = Array.isArray(response.data?.runtime_features)
        ? response.data.runtime_features
        : directFeatures;
      const st = response.data?.subscription_status;
      const usableFeatures = ['suspended', 'cancelled', 'purged'].includes(st)
        ? []
        : runtimeFeatures;
      setPlanFeatures(usableFeatures);
      setPlanGate(usableFeatures);
      setPlanError(false);
    } catch (error) {
      // iter244 SEC F4 — fail-CLOSED. Previously the UI defaulted to
      // all-features-enabled on network/5xx, which combined with the
      // ambient tenant fallback could expose Enterprise UI to
      // Starter-tier tenants. Now we flag the error and hide
      // gated UI until /tenant/me succeeds. Backend has enforced
      // 402 all along, so gated fetches would 402 anyway — the
      // UI hides them upfront instead of showing broken cards.
      setPlanGate(null);
      console.warn(
        '[FeatureContext] /tenant/me failed; fail-CLOSED — hiding plan-gated UI',
        error?.message,
      );
      setPlanFeatures([]);
      setPlanError(true);
    } finally {
      setPlanLoaded(true);
    }
  };

  const fetchFeatures = async () => {
    if (!token || !user) {
      setFeatures({});
      setLoading(false);
      return;
    }

    // Admin has all features enabled
    if (user.role === 'admin') {
      setFeatures({
        tournaments: true,
        lane_booking: true,
        pause_enrollment: true,
        cancel_enrollment: true,
        makeup_classes: true,
        view_attendance: true,
        movement_requests: true,
        billing_portal: true,
        uniform_ordering: true,
        mark_attendance: true,
        log_hours: true,
        submit_notes: true,
        view_billing_history: true,
        parent_onboarding: true,
        trial_management: true,
        enrollment_management: true,
        shift_scheduling: true,
      });
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/features/my-features');
      setFeatures(response.data.features || {});
    } catch (error) {
      console.error('Failed to fetch features:', error);
      // On error, enable all features as fallback
      setFeatures({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
    fetchPlanFeatures();
  }, [user, token]);

  const isFeatureEnabled = (featureKey) => {
    // If still loading or admin, allow all
    if (loading || user?.role === 'admin') return true;
    // If feature not in list, default to enabled (for backwards compatibility)
    if (!(featureKey in features)) return true;
    return features[featureKey] === true;
  };

  // iter244 SEC F4 — Plan-feature gate, fail-CLOSED after load.
  //
  // - Returns true while the plan list is still loading (avoids UI
  //   flicker on first paint; auth-boundary redirect already prevents
  //   unauth users from seeing pages).
  // - Returns true for super_admin (cross-tenant operator UI).
  // - After load, if `/tenant/me` errored → fail-CLOSED (returns false).
  // - After load, if the empty-list was intentionally set by the empty-
  //   token/no-user branch, that also fails closed (no user = no plan).
  // - Otherwise checks the dependency-expanded runtime set from
  //   `/tenant/me`, matching server-side feature enforcement.
  const isPlanFeatureEnabled = (planFeatureKey) => {
    if (!planFeatureKey) return true;
    if (!planLoaded) return true;
    if (user?.role === 'super_admin') return true;
    if (planError) return false;
    if (!planFeatures || planFeatures.length === 0) return false;
    return planFeatures.includes(planFeatureKey);
  };

  const refreshFeatures = () => {
    fetchFeatures();
    fetchPlanFeatures();
  };

  return (
    <FeatureContext.Provider
      value={{
        features,
        loading,
        isFeatureEnabled,
        planFeatures,
        isPlanFeatureEnabled,
        planLoaded,
        refreshFeatures,
      }}
    >
      {children}
    </FeatureContext.Provider>
  );
};

export default FeatureContext;
